/**
 * Wallet Shortcuts card for the modern Dialer (Phase A).
 *
 * Premium fintech-style wallet snapshot with quick links to Top Up,
 * Transfer Credit, and Transactions. Designed to be sticky-visible above the
 * keypad and to nudge low-balance users straight into top-up.
 */
import { Link } from "react-router-dom";
import { Plus, ArrowLeftRight, ListOrdered, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { gbp } from "@/lib/wallet";

export default function DialerWalletChip({ balance, rate, destinationName, isAuthed }) {
  return (
    <div
      className="relative overflow-hidden rounded-3xl px-5 py-4 text-white shadow-[0_18px_42px_-22px_rgba(7,27,77,0.7)]"
      data-testid="dialer-wallet-chip"
      style={{
        background:
          "radial-gradient(120% 140% at 0% 0%, #102f7a 0%, #071B4D 55%, #050f2e 100%)",
      }}
    >
      {/* Decorative grain ring */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.18]"
        style={{
          background:
            "radial-gradient(60% 100% at 100% 0%, rgba(0,208,90,0.55) 0%, transparent 60%)",
        }}
      />
      <div className="relative flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Wallet
          </p>
          {isAuthed ? (
            <p className="font-display font-black text-3xl mt-1 leading-none" data-testid="dialer-balance">
              {balance != null ? gbp(balance) : "…"}
            </p>
          ) : (
            <p className="font-display font-black text-3xl mt-1 leading-none text-white/35">£ — . — —</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold">Rate</p>
          <p className="font-display font-black text-lg mt-1 leading-none" data-testid="dialer-rate">
            {rate != null ? `${gbp(rate)}/min` : "—"}
          </p>
          {destinationName && (
            <p className="text-[10px] text-white/55 truncate max-w-[140px] mt-0.5">{destinationName}</p>
          )}
        </div>
      </div>

      {isAuthed && (
        <div className="relative mt-4 grid grid-cols-3 gap-1.5">
          <ShortcutLink to="/app/wallet/topup" icon={<Plus className="h-3.5 w-3.5" />} label="Top up" testId="wallet-shortcut-topup" />
          <ShortcutComingSoon icon={<ArrowLeftRight className="h-3.5 w-3.5" />} label="Transfer" testId="wallet-shortcut-transfer" />
          <ShortcutLink to="/app/wallet" icon={<ListOrdered className="h-3.5 w-3.5" />} label="History" testId="wallet-shortcut-history" />
        </div>
      )}
    </div>
  );
}

function ShortcutLink({ to, icon, label, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 px-2 py-2 text-[11px] font-bold text-white transition"
    >
      {icon}{label}
    </Link>
  );
}

function ShortcutComingSoon({ icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={() => toast.info(`${label}: Coming soon — landing in the next OneDial update.`)}
      data-testid={testId}
      className="relative inline-flex items-center justify-center gap-1.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 px-2 py-2 text-[11px] font-bold text-white/90 transition"
    >
      {icon}{label}
      <span className="absolute -top-1.5 -right-1.5 rounded-full bg-amber-400 text-amber-900 text-[8px] font-black px-1 leading-[12px] ring-2 ring-[#071B4D]">SOON</span>
    </button>
  );
}
